import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useListPlansQuery, useSelectPlanMutation, Plan } from '@/redux/internal-services/onboardingApi';
import { setSubscriptionStatus } from '@/redux/slices/sessionStateSlice';
import { useAppSelector } from '@/hooks';
import LeftSidePanel from '@/components/UI/LeftSidePanel';
import { getErrorDetail } from '@/utils/apiError';
import { requestOnboardingVoiceMode, setOnboardingVoiceMode } from './onboardingVoiceMode';
import { Button } from '@/components/UI/Button';
import { Spinner } from '@/components/UI/Spinner';
import { cn } from '@/utils/cn';

const PlanCard: React.FC<{
  plan: Plan;
  selected: boolean;
  billingCycle: 'monthly' | 'annual';
  busy: boolean;
  onSelect: () => void;
  onGetStarted: () => void;
}> = ({ plan, selected, billingCycle, busy, onSelect, onGetStarted }) => {
  const price = billingCycle === 'annual' ? plan.price_annual : plan.price_monthly;
  const isPopular = (plan as Plan & { is_popular?: boolean }).is_popular;
  // Only the currently selected card is raised and ringed in violet (single selection); its button is the
  // one orange call to action on the page, the others are outlined.

  return (
    <div
      className={cn(
        'card-surface relative flex h-full cursor-pointer flex-col p-6 transition-all',
        selected
          ? 'z-10 border-accent ring-2 ring-accent/40 shadow-glow-accent lg:scale-[1.03]'
          : 'hover:border-ui-border3',
      )}
      onClick={onSelect}
    >
      {isPopular && (
        <div
          className={cn(
            'absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold tracking-wide',
            selected ? 'bg-accent-fill text-on-accent' : 'bg-accent-soft text-accent-text',
          )}
        >
          MOST POPULAR
        </div>
      )}

      <div className="mb-4">
        <p className="text-3xl font-bold text-ui-text sm:text-4xl">
          ${price ?? 0}
          <span className="text-base font-normal text-ui-text2">{billingCycle === 'annual' ? '/yr' : '/mo'}</span>
        </p>
        <h3 className="mt-2 text-h3 text-ui-text">{plan.name}</h3>
        <p className="mt-1 text-sm text-ui-text2">{plan.description}</p>
      </div>

      {plan.features && plan.features.length > 0 && (
        <ul className="flex-1 space-y-2.5">
          {plan.features.map((f) => (
            <li key={f} className="flex items-center gap-2.5 text-sm">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-text">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span className="text-ui-text2">{f}</span>
            </li>
          ))}
        </ul>
      )}

      <Button
        size="lg"
        variant={selected ? 'primary' : 'outline'}
        onClick={(e) => { e.stopPropagation(); onGetStarted(); }}
        disabled={busy}
        className="mt-6 w-full"
      >
        Get Started
      </Button>
    </div>
  );
};

const PlanSelectionPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data, isLoading } = useListPlansQuery();
  const [selectPlan, { isLoading: isSelecting }] = useSelectPlanMutation();

  // Once SAM welcome has fired for this user, plan selection is off-limits — a returning
  // member who lands here by URL or stale link must go straight to /chat.
  const samWelcomeShown = useAppSelector((s) => s.sessionState.samWelcomeShown);
  useEffect(() => {
    if (samWelcomeShown) navigate('/chat', { replace: true });
  }, [samWelcomeShown, navigate]);

  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [error, setError] = useState('');

  const plans = data?.data ?? [];

  // Default the selection to the popular plan (falling back to the first) once plans arrive.
  useEffect(() => {
    if (!selectedPlanId && plans.length) {
      const popular = plans.find((p) => (p as Plan & { is_popular?: boolean }).is_popular);
      setSelectedPlanId((popular ?? plans[0]).plan_id);
    }
  }, [plans, selectedPlanId]);

  const activate = async (planId: string, cycle: 'monthly' | 'annual') => {
    await selectPlan({ plan_id: planId, billing_cycle: cycle }).unwrap();
    dispatch(setSubscriptionStatus('active'));
    // Ask for the mic now that the plan is chosen: granted → SAM guides the setup by voice;
    // denied/unsupported → the setup wizard falls back to the manual form.
    setOnboardingVoiceMode(await requestOnboardingVoiceMode());
    navigate('/onboarding/setup');
  };

  // Clicking a card only selects it (brings it forward) — it does not activate the plan.
  const handleSelect = (planId: string) => {
    setSelectedPlanId(planId);
    setError('');
  };

  // Activation happens only via the "Get Started" button on a card.
  const handleGetStarted = async (planId: string) => {
    setSelectedPlanId(planId);
    setError('');
    try {
      await activate(planId, billingCycle);
    } catch (err: unknown) {
      setError(getErrorDetail(err) ?? t('common.error'));
    }
  };

  // "Skip this step" — start the 14-day free trial on the trial-eligible plan so the user can
  // explore now and switch plans later from Settings. Routing requires an activated subscription,
  // so we activate the trial plan rather than navigating with no subscription (which bounces back).
  const handleSkip = async () => {
    setError('');
    const trialPlan = plans.find((p) => p.trial_eligible) ?? plans[0];
    if (!trialPlan) { navigate('/onboarding/setup'); return; }
    try {
      await activate(trialPlan.plan_id, billingCycle);
    } catch (err: unknown) {
      setError(getErrorDetail(err) ?? t('common.error'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ui">
        <Spinner size="lg" />
        <span className="sr-only">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex bg-ui">
      {/* Left panel — shared app-wide branded side panel */}
      <LeftSidePanel />

      {/* Right panel — plan selection */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <h1 className="text-h2 text-ui-text mb-7">Choose what works for your team</h1>

        {/* Billing toggle */}
        <div role="group" aria-label="Billing period" className="mb-10 flex rounded-full border border-ui-border bg-ui-card p-1">
          {(['monthly', 'annual'] as const).map((cycle) => (
            <button
              key={cycle}
              onClick={() => setBillingCycle(cycle)}
              aria-pressed={billingCycle === cycle}
              className={`rounded-full px-6 py-1.5 text-sm font-semibold capitalize transition ${
                billingCycle === cycle ? 'bg-accent-fill text-on-accent' : 'text-ui-text2 hover:text-ui-text'
              }`}
            >
              {cycle === 'annual' ? 'Yearly' : 'Monthly'}
            </button>
          ))}
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 w-full max-w-4xl items-stretch">
          {plans.map((plan) => (
            <PlanCard
              key={plan.plan_id}
              plan={plan}
              selected={selectedPlanId === plan.plan_id}
              billingCycle={billingCycle}
              busy={isSelecting}
              onSelect={() => handleSelect(plan.plan_id)}
              onGetStarted={() => handleGetStarted(plan.plan_id)}
            />
          ))}
        </div>

        {error && <p role="alert" className="mt-4 text-sm text-danger-text">{error}</p>}

        <Button onClick={handleSkip}
          disabled={isSelecting}
          variant="link" className="mt-8">
          Skip this step
        </Button>
      </div>
    </div>
  );
};

export default PlanSelectionPage;
